/**
 * Movement-related particle effects — bounce, wall bounce, direction change, climb dust, launch trail.
 */

import { WALL_BOUNCE, CEILING_BOUNCE, CLIMB_FX, LAUNCH_TRAIL, HOP, FAST_FALL } from '../../constants';
import { configParticle, type ParticleCore } from './types';

// Frame stagger counter for lift trail (energy cape pattern — halves per-frame particle count)
let liftTrailFrame = 0;

/**
 * Ragdoll bounce impact — anime-style layered contact effect.
 * Sakurai audit: combines bright sparks (contrast) + dark debris (depth) + impact lines (crisp geometry)
 * + small shockwave ring. Intensity scales with bounce number (first bounce = biggest).
 */
export function spawnBounceImpact(core: ParticleCore, cx: number, cy: number, playerColor: number, intensity: number): void {
  // intensity: 1.0 = first bounce, ~0.5 = second, ~0.25 = third

  // Layer 1: Dark terrain debris (existing pattern — grounding, Sakurai dark contrast)
  const debrisCount = Math.max(4, Math.floor(8 * intensity));
  const debrisColors = [0x0a0c1e, 0x0c0e28, 0x1a1e40, 0x222850];
  for (let i = 0; i < debrisCount; i++) {
    const color = debrisColors[Math.floor(Math.random() * debrisColors.length)];
    const p = core.getInactive();
    if (!p) continue;
    configParticle(p, core, {
      x: cx + (Math.random() - 0.5) * 20,
      y: cy,
      vx: (Math.random() - 0.5) * 4 * intensity,
      vy: -(1 + Math.random() * 3) * intensity, // upward — kicked up by impact
      color,
      life: 0.2 + Math.random() * 0.2,
      scale: (0.15 + Math.random() * 0.3) * intensity,
      alpha: 0.7 + Math.random() * 0.2,
      gravity: 0.18,
      friction: 0.96,
      rotationSpeed: (Math.random() - 0.5) * 12,
      addBlend: false,
    });
  }

  // Layer 2: Bright sparks — ADD blend, white + player color (Sakurai: contrast against dark debris)
  const sparkCount = Math.max(3, Math.floor(6 * intensity));
  for (let i = 0; i < sparkCount; i++) {
    const sparkColor = Math.random() < 0.4 ? 0xffffff : playerColor;
    core.spawnParticle(
      cx, cy, sparkColor,
      3 * intensity, 8 * intensity,                  // speedMin, speedMax
      0.08, 0.15,                                     // lifeMin, lifeMax — very brief
      0.4 * intensity, (0.4 + 0.6) * intensity,      // scaleMin, scaleMax
      0.12, 0.94,                                     // gravity, friction
      true, true,                                     // ADD blend, hard texture
      0.9,                                            // alpha
      1.8,                                            // stretch — anime speed lines feel
      4,                                              // spawn offset
    );
  }

  // Layer 3: Hot flash pop — single brief soft glow at contact point (Sakurai: flash communicates impact)
  if (intensity > 0.4) {
    core.spawnParticle(
      cx, cy, playerColor,
      0, 0.5,                                         // speedMin, speedMax
      0.06, 0.1,                                      // lifeMin, lifeMax — ultra-brief pop
      2.5 * intensity, 3.5 * intensity,               // scaleMin, scaleMax
      0, 0.3,                                         // gravity, friction
      true, false,                                    // ADD, soft texture — diffuse glow
      0.6 * intensity,                                // alpha
    );
  }

  // Layer 4: Crisp impact lines — Sakurai "straight lines vs curves" contrast
  const lineCount = Math.max(2, Math.floor(4 * intensity));
  for (let i = 0; i < lineCount; i++) {
    // Radiate upward in a fan (not full 360 — ground contact, so upper hemisphere)
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
    const life = 0.1 + 0.05 * intensity;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 4,
      outerRadius: (15 + Math.random() * 15) * intensity,
      expandSpeed: (40 + Math.random() * 30) * intensity,
      color: Math.random() < 0.3 ? 0xffffff : playerColor,
      lineWidth: 1.5 + intensity,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // Layer 5: Tiny shockwave ring — anime ground impact (only on strong bounces)
  if (intensity > 0.5) {
    core.spawnShockwave(
      cx, cy,
      playerColor,
      20 * intensity,   // small radius
      120,              // fast expand
      1.5,              // thin line
      0.12,             // very brief
    );
  }
}

/**
 * Wall bounce — pinball bumper at world edges.
 * Sakurai-audited multi-layer blade/platform catch effect.
 *
 * Layer 1: Blade flash — vertical bright line at contact (terrain edge cyan / spike red)
 * Layer 2: Directional sparks — fanning inward from wall toward center
 * Layer 3: Dark debris — Sakurai contrast (dark chunks kicked inward)
 * Layer 4: Impact lines — crisp straight lines radiating inward
 * Layer 5: Half-ring shockwave — expanding semicircle (inward from wall)
 * Layer 6: Flash pop — soft glow at contact point
 * Spike special: Cross slash (X) at contact — red-hot, aggressive
 */
export function spawnWallBounce(
  core: ParticleCore,
  cx: number, cy: number,
  inwardDir: number,       // 1 = left wall (sparks go right), -1 = right wall (sparks go left)
  playerColor: number,
  intensity: number,       // 0..1 based on impact speed
  isSpike: boolean,
): void {
  const bladeColor = isSpike ? WALL_BOUNCE.SPIKE_COLOR : WALL_BOUNCE.BLADE_COLOR;
  const darkColor = isSpike ? WALL_BOUNCE.SPIKE_DARK_COLOR : WALL_BOUNCE.BLADE_DARK_COLOR;
  const inwardAngle = inwardDir > 0 ? 0 : Math.PI; // horizontal angle toward center
  const now = Date.now();

  // === Layer 1: Blade flash — vertical bright line at wall contact ===
  // Dark backing line (Sakurai: mix bright + dark for any-background visibility)
  // freezeImmune: blade flash animates through hitstop (Sakurai: "hit markers continue to move")
  const bladeHeight = WALL_BOUNCE.BLADE_WIDTH * intensity;
  for (let i = 0; i < 2; i++) {
    const isDark = i === 0;
    const life = isDark ? 0.2 : 0.18;
    core.impactLines.push({
      x: cx, y: cy,
      angle: -Math.PI / 2 + (Math.random() - 0.5) * 0.15, // ~vertical with slight variance
      innerRadius: 0,
      outerRadius: bladeHeight * (isDark ? 1.1 : 1.0),
      expandSpeed: isDark ? 20 : 35,
      color: isDark ? darkColor : bladeColor,
      lineWidth: isDark ? 5 : 3,
      life,
      maxLife: life,
      freezeImmune: true,
    });
    // Mirror below
    core.impactLines.push({
      x: cx, y: cy,
      angle: Math.PI / 2 + (Math.random() - 0.5) * 0.15,
      innerRadius: 0,
      outerRadius: bladeHeight * (isDark ? 1.1 : 1.0),
      expandSpeed: isDark ? 20 : 35,
      color: isDark ? darkColor : bladeColor,
      lineWidth: isDark ? 5 : 3,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }

  // === Layer 2: Directional sparks — fan inward from wall toward center ===
  const sparkCount = isSpike ? WALL_BOUNCE.SPIKE_SPARK_COUNT : WALL_BOUNCE.SPARK_COUNT;
  const scaledSparkCount = Math.max(3, Math.floor(sparkCount * intensity));
  for (let i = 0; i < scaledSparkCount; i++) {
    const sparkColor = Math.random() < 0.3 ? 0xffffff
      : Math.random() < 0.5 ? playerColor
        : (isSpike ? WALL_BOUNCE.SPIKE_COLOR : WALL_BOUNCE.BLADE_COLOR);
    const angle = inwardAngle + (Math.random() - 0.5) * Math.PI * 0.7; // ±63° fan
    const speed = (4 + Math.random() * 7) * intensity;
    core.spawnParticleAtAngle(
      cx + inwardDir * (2 + Math.random() * 4),  // slight offset from wall
      cy + (Math.random() - 0.5) * bladeHeight * 0.6,
      angle, speed,
      sparkColor,
      0.08 + Math.random() * 0.12,  // brief
      (0.3 + Math.random() * 0.4) * intensity,
      0.06, 0.93,
      true, true,   // ADD, hard
      0.9,
      1.6 + Math.random() * 0.8,  // stretch — anime speed lines
    );
  }

  // === Layer 3: Dark debris — Sakurai dark contrast (chunks kicked inward) ===
  // Staggered +40ms (Sakurai: "shift the timing" — debris follows sparks)
  core.pendingBlasts.push({
    timestamp: now + 40, callback: () => {
      const debrisCount = Math.max(3, Math.floor(6 * intensity));
      const debrisColors = [0x0a0c1e, 0x0c0e28, 0x1a1e40, 0x222850];
      for (let i = 0; i < debrisCount; i++) {
        const color = debrisColors[Math.floor(Math.random() * debrisColors.length)];
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
          x: cx + inwardDir * (Math.random() * 12),  // wider spread (Sakurai: avoid center pile-up)
          y: cy + (Math.random() - 0.5) * 24,
          vx: inwardDir * (1 + Math.random() * 3) * intensity,
          vy: -(0.5 + Math.random() * 2) * intensity, // slight upward
          color,
          life: 0.2 + Math.random() * 0.2,
          scale: (0.15 + Math.random() * 0.25) * intensity,
          alpha: 0.7 + Math.random() * 0.2,
          gravity: 0.15,
          friction: 0.95,
          rotationSpeed: (Math.random() - 0.5) * 10,
          addBlend: false,
        });
      }
    }
  });

  // === Layer 4: Impact lines — crisp straight lines radiating inward ===
  const lineCount = isSpike ? WALL_BOUNCE.SPIKE_IMPACT_LINE_COUNT : WALL_BOUNCE.IMPACT_LINE_COUNT;
  const scaledLineCount = Math.max(2, Math.floor(lineCount * intensity));
  for (let i = 0; i < scaledLineCount; i++) {
    const angle = inwardAngle + (Math.random() - 0.5) * Math.PI * 0.6;
    const life = 0.1 + 0.05 * intensity;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 4,
      outerRadius: (18 + Math.random() * 18) * intensity,
      expandSpeed: (50 + Math.random() * 40) * intensity,
      color: Math.random() < 0.3 ? 0xffffff : bladeColor,
      lineWidth: 1.5 + intensity,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // === Layer 5: Half-ring shockwave — semicircle expanding inward from wall ===
  // Staggered +30ms (Sakurai: phased handoff — blade+sparks → shockwave → debris)
  const shockRadius = isSpike ? WALL_BOUNCE.SPIKE_SHOCKWAVE_RADIUS : WALL_BOUNCE.SHOCKWAVE_RADIUS;
  if (intensity > 0.3) {
    core.pendingBlasts.push({
      timestamp: now + 30, callback: () => {
        core.spawnShockwave(
          cx, cy,
          bladeColor,
          shockRadius * intensity,
          150,
          2,
          0.15,
        );
      }
    });
  }

  // === Layer 6: Flash pop — soft glow at contact (Sakurai: flash = impact notification) ===
  if (intensity > 0.3) {
    core.spawnParticle(
      cx, cy, bladeColor,
      0, 0.5,
      0.06, 0.1,
      (2.0 + 1.5 * intensity), (3.0 + 2.0 * intensity),
      0, 0.3,
      true, false,  // ADD, soft
      (isSpike ? 0.7 : 0.5) * intensity,
      1,
      4, // spawnOffset — reduce center pile-up (Sakurai: offset along travel direction)
    );
  }

  // === End punctuation snap — Sakurai: "sudden disappearance" termination ===
  // Delayed +180ms: hard bright pop to visually end the sequence (not a slow fade)
  core.pendingBlasts.push({
    timestamp: now + 180, callback: () => {
      const snapColor = isSpike ? WALL_BOUNCE.SPIKE_COLOR : bladeColor;
      core.spawnParticle(
        cx, cy, snapColor,
        0, 0.2,
        0.03, 0.05,    // ultra-brief snap
        (1.0 + 0.8 * intensity), (1.5 + 1.0 * intensity),
        0, 0.3,
        true, true,     // ADD, hard — crisp end mark
        (isSpike ? 0.6 : 0.4) * intensity,
      );
      // End snap impact line — single bright inward line
      const endLife = 0.06;
      core.impactLines.push({
        x: cx, y: cy,
        angle: inwardAngle,
        innerRadius: 0,
        outerRadius: (12 + 8 * intensity),
        expandSpeed: 80,
        color: snapColor,
        lineWidth: 2,
        life: endLife,
        maxLife: endLife,
        freezeImmune: true,
      });
    }
  });

  // === Spike special: Cross slash (X mark) — aggressive hit mark ===
  if (isSpike && intensity > 0.4) {
    // Two crossing blade lines — red-hot, thick, brief
    // Sakurai hit marks: "flashiness matching the effect"
    // freezeImmune: cross-slash animates through hitstop like consume cross
    for (let i = 0; i < 2; i++) {
      const slashAngle = (i === 0 ? -Math.PI / 4 : Math.PI / 4) + (Math.random() - 0.5) * 0.2;
      const life = 0.18;
      // Bright slash
      core.impactLines.push({
        x: cx + inwardDir * 5, y: cy,
        angle: slashAngle,
        innerRadius: 0,
        outerRadius: 35 * intensity,
        expandSpeed: 60,
        color: WALL_BOUNCE.SPIKE_COLOR,
        lineWidth: 3.5,
        life,
        maxLife: life,
        freezeImmune: true,
      });
      // Mirror slash (both arms of the X)
      core.impactLines.push({
        x: cx + inwardDir * 5, y: cy,
        angle: slashAngle + Math.PI,
        innerRadius: 0,
        outerRadius: 35 * intensity,
        expandSpeed: 60,
        color: WALL_BOUNCE.SPIKE_COLOR,
        lineWidth: 3.5,
        life,
        maxLife: life,
        freezeImmune: true,
      });
    }

    // Dark backing for cross slash (Sakurai: dark behind bright)
    for (let i = 0; i < 2; i++) {
      const slashAngle = (i === 0 ? -Math.PI / 4 : Math.PI / 4);
      const life = 0.2;
      core.impactLines.push({
        x: cx + inwardDir * 5, y: cy,
        angle: slashAngle,
        innerRadius: 0,
        outerRadius: 40 * intensity,
        expandSpeed: 50,
        color: WALL_BOUNCE.SPIKE_DARK_COLOR,
        lineWidth: 6,
        life,
        maxLife: life,
        freezeImmune: true,
      });
      core.impactLines.push({
        x: cx + inwardDir * 5, y: cy,
        angle: slashAngle + Math.PI,
        innerRadius: 0,
        outerRadius: 40 * intensity,
        expandSpeed: 50,
        color: WALL_BOUNCE.SPIKE_DARK_COLOR,
        lineWidth: 6,
        life,
        maxLife: life,
        freezeImmune: true,
      });
    }

    // Extra spike sparks — red-tinted, aggressive
    for (let i = 0; i < 4; i++) {
      const angle = (Math.random() - 0.5) * Math.PI * 2;
      core.spawnParticleAtAngle(
        cx + inwardDir * 5, cy,
        angle, 5 + Math.random() * 8,
        Math.random() < 0.5 ? WALL_BOUNCE.SPIKE_COLOR : 0xffffff,
        0.06 + Math.random() * 0.08,
        0.5 + Math.random() * 0.4,
        0.08, 0.92,
        true, true,
        0.9,
        2.0,
      );
    }
  }
}

/**
 * Ceiling bounce impact — rotated variant of wall bounce (sparks fan downward).
 * Sakurai audit: blade flash (contrast) + directional sparks + dark debris + impact lines
 * + half-ring shockwave + flash pop. No spike variant — simpler than wall bounce.
 */
export function spawnCeilingBounce(
  core: ParticleCore,
  cx: number, cy: number,
  playerColor: number,
  intensity: number,       // 0..1 based on impact speed
): void {
  const bladeColor = CEILING_BOUNCE.BLADE_COLOR;
  const darkColor = CEILING_BOUNCE.BLADE_DARK_COLOR;
  const now = Date.now();

  // === Layer 1: Blade flash — horizontal bright line at ceiling contact ===
  const bladeWidth = CEILING_BOUNCE.BLADE_WIDTH * intensity;
  for (let i = 0; i < 2; i++) {
    const isDark = i === 0;
    const life = isDark ? 0.2 : 0.18;
    // Left arm
    core.impactLines.push({
      x: cx, y: cy,
      angle: Math.PI + (Math.random() - 0.5) * 0.15,  // ~horizontal left
      innerRadius: 0,
      outerRadius: bladeWidth * (isDark ? 1.1 : 1.0),
      expandSpeed: isDark ? 20 : 35,
      color: isDark ? darkColor : bladeColor,
      lineWidth: isDark ? 5 : 3,
      life,
      maxLife: life,
      freezeImmune: true,
    });
    // Right arm
    core.impactLines.push({
      x: cx, y: cy,
      angle: 0 + (Math.random() - 0.5) * 0.15,  // ~horizontal right
      innerRadius: 0,
      outerRadius: bladeWidth * (isDark ? 1.1 : 1.0),
      expandSpeed: isDark ? 20 : 35,
      color: isDark ? darkColor : bladeColor,
      lineWidth: isDark ? 5 : 3,
      life,
      maxLife: life,
      freezeImmune: true,
    });
  }

  // === Layer 2: Directional sparks — fan downward from ceiling ===
  const scaledSparkCount = Math.max(3, Math.floor(CEILING_BOUNCE.SPARK_COUNT * intensity));
  for (let i = 0; i < scaledSparkCount; i++) {
    const sparkColor = Math.random() < 0.3 ? 0xffffff
      : Math.random() < 0.5 ? playerColor : bladeColor;
    const angle = Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.7; // ±63° fan downward
    const speed = (4 + Math.random() * 7) * intensity;
    core.spawnParticleAtAngle(
      cx + (Math.random() - 0.5) * bladeWidth * 0.6,
      cy + (2 + Math.random() * 4),  // slight offset from ceiling
      angle, speed,
      sparkColor,
      0.08 + Math.random() * 0.12,
      (0.3 + Math.random() * 0.4) * intensity,
      0.06, 0.93,
      true, true,   // ADD, hard
      0.9,
      1.6 + Math.random() * 0.8,  // stretch — anime speed lines
    );
  }

  // === Layer 3: Dark debris — Sakurai dark contrast (chunks falling down) ===
  core.pendingBlasts.push({
    timestamp: now + 40, callback: () => {
      const debrisCount = Math.max(3, Math.floor(6 * intensity));
      const debrisColors = [0x0a0c1e, 0x0c0e28, 0x1a1e40, 0x222850];
      for (let i = 0; i < debrisCount; i++) {
        const color = debrisColors[Math.floor(Math.random() * debrisColors.length)];
        const p = core.getInactive();
        if (!p) continue;
        configParticle(p, core, {
          x: cx + (Math.random() - 0.5) * 24,
          y: cy + (Math.random() * 12),
          vx: (Math.random() - 0.5) * 3 * intensity,
          vy: (1 + Math.random() * 3) * intensity, // downward
          color,
          life: 0.2 + Math.random() * 0.2,
          scale: (0.15 + Math.random() * 0.25) * intensity,
          alpha: 0.7 + Math.random() * 0.2,
          gravity: 0.15,
          friction: 0.95,
          rotationSpeed: (Math.random() - 0.5) * 10,
          addBlend: false,
        });
      }
    }
  });

  // === Layer 4: Impact lines — crisp lines radiating downward ===
  const scaledLineCount = Math.max(2, Math.floor(CEILING_BOUNCE.IMPACT_LINE_COUNT * intensity));
  for (let i = 0; i < scaledLineCount; i++) {
    const angle = Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6; // downward fan
    const life = 0.1 + 0.05 * intensity;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 4,
      outerRadius: (18 + Math.random() * 18) * intensity,
      expandSpeed: (50 + Math.random() * 40) * intensity,
      color: Math.random() < 0.3 ? 0xffffff : bladeColor,
      lineWidth: 1.5 + intensity,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // === Layer 5: Half-ring shockwave — semicircle expanding downward from ceiling ===
  if (intensity > 0.3) {
    core.pendingBlasts.push({
      timestamp: now + 30, callback: () => {
        core.spawnShockwave(
          cx, cy,
          bladeColor,
          CEILING_BOUNCE.SHOCKWAVE_RADIUS * intensity,
          150,
          2,
          0.15,
        );
      }
    });
  }

  // === Layer 6: Flash pop — soft glow at contact ===
  if (intensity > 0.3) {
    core.spawnParticle(
      cx, cy, bladeColor,
      0, 0.5,
      0.06, 0.1,
      (2.0 + 1.5 * intensity), (3.0 + 2.0 * intensity),
      0, 0.3,
      true, false,  // ADD, soft
      0.5 * intensity,
      1,
      4,
    );
  }

  // === End punctuation snap ===
  core.pendingBlasts.push({
    timestamp: now + 180, callback: () => {
      core.spawnParticle(
        cx, cy, bladeColor,
        0, 0.2,
        0.03, 0.05,
        (1.0 + 0.8 * intensity), (1.5 + 1.0 * intensity),
        0, 0.3,
        true, true,
        0.4 * intensity,
      );
      const endLife = 0.06;
      core.impactLines.push({
        x: cx, y: cy,
        angle: Math.PI / 2,  // downward
        innerRadius: 0,
        outerRadius: (12 + 8 * intensity),
        expandSpeed: 80,
        color: bladeColor,
        lineWidth: 2,
        life: endLife,
        maxLife: endLife,
        freezeImmune: true,
      });
    }
  });
}

/**
 * Direction change skid effect — anime wavedash feel.
 * Triggered on grounded direction reversal while already moving.
 *
 * Layer 1: Dark dust kicked in old direction (NORMAL blend — Sakurai dark contrast)
 * Layer 2: Bright sparks shooting in old direction (ADD blend — anime speed feel)
 * Layer 3: Ground skid lines radiating in old direction (Sakurai straight lines vs curves)
 */
export function spawnDirectionChange(core: ParticleCore, cx: number, cy: number, oldDir: number, playerColor: number): void {
  const dirAngle = oldDir > 0 ? 0 : Math.PI; // horizontal angle in old direction

  // Layer 1: Dark dust — 4-5 NORMAL blend particles kicked in oldDir
  const dustCount = 4 + Math.floor(Math.random() * 2);
  const dustColors = [0x0a0c1e, 0x0c0e28, 0x1a1e40];
  for (let i = 0; i < dustCount; i++) {
    const color = dustColors[Math.floor(Math.random() * dustColors.length)];
    const p = core.getInactive();
    if (!p) continue;
    const spread = (Math.random() - 0.5) * 0.6;
    const speed = 1.5 + Math.random() * 2.5;
    configParticle(p, core, {
      x: cx + oldDir * (3 + Math.random() * 8),
      y: cy - Math.random() * 4,
      vx: Math.cos(dirAngle + spread) * speed,
      vy: -(0.5 + Math.random() * 1.5), // slight upward arc
      color,
      life: 0.15 + Math.random() * 0.1,
      scale: 0.2 + Math.random() * 0.25,
      alpha: 0.6 + Math.random() * 0.2,
      gravity: 0.12,
      friction: 0.94,
      rotationSpeed: (Math.random() - 0.5) * 8,
      addBlend: false,
    });
  }

  // Layer 2: Bright sparks — 2-3 ADD blend, stretched, white + playerColor
  const sparkCount = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < sparkCount; i++) {
    const sparkColor = Math.random() < 0.4 ? 0xffffff : playerColor;
    const spread = (Math.random() - 0.5) * 0.5; // ±15°
    core.spawnParticle(
      cx + oldDir * 5, cy,
      sparkColor,
      4, 8,           // speedMin, speedMax
      0.06, 0.1,      // lifeMin, lifeMax — very brief
      0.3, 0.5,       // scaleMin, scaleMax
      0.05, 0.92,     // gravity, friction
      true, true,     // ADD blend, hard texture
      0.85,           // alpha
      1.5,            // stretch — anime speed lines
      3,              // spawn offset
    );
  }

  // Layer 3: Ground skid lines — 2-3 impact lines
  const lineCount = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < lineCount; i++) {
    const spread = (Math.random() - 0.5) * 0.3;
    const life = 0.06 + Math.random() * 0.04;
    core.impactLines.push({
      x: cx, y: cy,
      angle: dirAngle + spread,
      innerRadius: 5,
      outerRadius: 15 + Math.random() * 10,
      expandSpeed: 80,
      color: Math.random() < 0.3 ? 0xffffff : playerColor,
      lineWidth: 1.5,
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }
}

/**
 * Climb dust burst — 5-layer anime effort effect on steep uphill climbs.
 *
 * Sakurai audit:
 * - Dark+bright contrast (layers 1+2)
 * - Straight lines vs curves (layer 5 impact lines)
 * - Flash communicates impact (layer 3 contact glow)
 * - Offset spawns avoid center overlap waste
 * - Sized to read against ECB_RADIUS=25 (no candle flames)
 * - Short lifespans, rough and bold
 */
export function spawnClimbDust(core: ParticleCore, x: number, feetY: number, climbDir: number, playerColor: number): void {
  // kickDir: dark dust gets kicked OPPOSITE to climb (behind the tank)
  const kickAngle = climbDir > 0 ? Math.PI * 0.8 : Math.PI * 0.2; // diagonal back-and-up

  // Layer 1: Dark dust — kicked behind tank (NORMAL blend — Sakurai dark contrast framing)
  const dustColors = [CLIMB_FX.DUST_DARK_COLOR, 0x0c0e28, 0x1a1e40];
  for (let i = 0; i < CLIMB_FX.DUST_DARK_COUNT; i++) {
    const p = core.getInactive();
    if (!p) continue;
    // Offset spawns along kick direction — avoid center overlap (Sakurai)
    const spawnSpread = Math.random() * 12;
    const speed = 1.5 + Math.random() * 2.5;
    const spread = (Math.random() - 0.5) * 0.8;
    const frames = CLIMB_FX.DUST_DARK_LIFETIME;
    configParticle(p, core, {
      x: x - climbDir * (4 + spawnSpread) + (Math.random() - 0.5) * 8,
      y: feetY - Math.random() * 6,
      vx: Math.cos(kickAngle + spread) * speed,
      vy: Math.sin(kickAngle + spread) * speed - 0.8, // upward bias
      color: dustColors[Math.floor(Math.random() * dustColors.length)],
      life: frames / 60,
      scale: 0.3 + Math.random() * 0.4, // 0.3-0.7 — visible against tank
      alpha: 0.7 + Math.random() * 0.2,
      gravity: 0.12,
      friction: 0.94,
      rotationSpeed: (Math.random() - 0.5) * 8,
      addBlend: false,
    });
  }

  // Layer 2: Bright sparks — fan upward, biased toward climb (ADD blend, Sakurai bright element)
  for (let i = 0; i < CLIMB_FX.DUST_SPARK_COUNT; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4; // upward fan ±40°
    const biasedAngle = angle + climbDir * 0.3;
    const speed = 4 + Math.random() * 6;
    const frames = CLIMB_FX.DUST_SPARK_LIFETIME;
    // Offset spawn positions along travel direction (Sakurai: avoid center overlap)
    const ox = Math.cos(biasedAngle) * (3 + Math.random() * 6);
    const oy = Math.sin(biasedAngle) * (3 + Math.random() * 6);
    core.spawnParticleAtAngle(
      x + ox, feetY + oy,
      biasedAngle, speed,
      Math.random() < 0.35 ? 0xffffff : playerColor,
      frames / 60,
      0.4 + Math.random() * 0.4, // 0.4-0.8 scale — bold, not candle flames
      0.06, 0.92,
      true, true,
      1.0, 2.0, // high stretch — anime effort lines
    );
  }

  // Layer 3: Contact flash pop — brief glow at feet (Sakurai: flash communicates impact instantly)
  core.spawnParticle(
    x, feetY, playerColor,
    0, 0.5,             // near-static
    0.06, 0.10,         // ultra-brief pop
    2.0, 2.8,           // large — reads as ground glow
    0, 0.3,
    true, false,        // ADD blend, soft texture (diffuse glow)
    0.5,
  );

  // Layer 4: Speed lines — fast upward streaks (Sakurai: "rough and fast")
  for (let i = 0; i < CLIMB_FX.DUST_LINE_COUNT; i++) {
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 0.5 + climbDir * 0.2;
    const speed = 8 + Math.random() * 5;
    const frames = CLIMB_FX.DUST_LINE_LIFETIME;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 10, feetY,
      angle, speed,
      0xffffff,
      frames / 60,
      0.3 + Math.random() * 0.2, // 0.3-0.5 — visible streaks
      0, 0.96,
      true, true,
      0.85, 3.5, // high stretch — line effect
    );
  }

  // Layer 5: Impact lines — crisp straight lines radiating from feet
  // (Sakurai: "adding straight lines to curves creates contrast")
  const lineCount = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < lineCount; i++) {
    // Upper hemisphere fan, biased opposite to climb (kick-back feel)
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.7 - climbDir * 0.3;
    const life = 0.08 + Math.random() * 0.05;
    core.impactLines.push({
      x, y: feetY,
      angle,
      innerRadius: 5,
      outerRadius: 18 + Math.random() * 12,
      expandSpeed: 60 + Math.random() * 40,
      color: Math.random() < 0.3 ? 0xffffff : playerColor,
      lineWidth: 1.5 + Math.random(),
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }
}

/**
 * Launch trail — speed-scaled particles during ragdoll flight.
 * 4-tier system: wisps → wisps+smoke → streaks+smoke+lines → full KO burst.
 * Sakurai: scale to event, dark+bright contrast, short lifespans, staggered density.
 */
export function spawnLaunchTrail(core: ParticleCore, x: number, y: number, vx: number, vy: number, playerColor: number, speed: number): void {
  const T = LAUNCH_TRAIL;
  const angle = Math.atan2(vy, vx);
  const backAngle = angle + Math.PI;

  // Determine tier
  const tier = speed >= T.TIER_KO ? 4 : speed >= T.TIER_HIGH ? 3 : speed >= T.TIER_MED ? 2 : 1;

  // --- Wisps (all tiers) — soft glow, player color, ADD blend ---
  const wispCount = tier === 1 ? 1 + Math.floor(Math.random() * 2) :
                    tier === 2 ? 2 + Math.floor(Math.random() * 2) :
                    tier === 3 ? 3 + Math.floor(Math.random() * 2) :
                                 4 + Math.floor(Math.random() * 3);
  for (let i = 0; i < wispCount; i++) {
    const ox = (Math.random() - 0.5) * 16;
    const oy = (Math.random() - 0.5) * 16;
    const life = T.WISP_LIFE_MIN + Math.random() * (T.WISP_LIFE_MAX - T.WISP_LIFE_MIN);
    const scale = T.WISP_SCALE_MIN + Math.random() * (T.WISP_SCALE_MAX - T.WISP_SCALE_MIN);
    core.spawnParticleAtAngle(
      x + ox, y + oy,
      Math.random() * Math.PI * 2, 0.3 + Math.random() * 0.5,
      Math.random() < 0.3 ? 0xffffff : playerColor,
      life, scale, 0, 0.93,
      true, false, T.WISP_ALPHA,
    );
  }

  // --- Dark smoke (tier 2+) — NORMAL blend, soft glow, slight upward drift ---
  if (tier >= 2) {
    const smokeCount = tier === 2 ? 1 : tier === 3 ? 2 : 3;
    for (let i = 0; i < smokeCount; i++) {
      const ox = (Math.random() - 0.5) * 12;
      const oy = (Math.random() - 0.5) * 12;
      const life = T.SMOKE_LIFE_MIN + Math.random() * (T.SMOKE_LIFE_MAX - T.SMOKE_LIFE_MIN);
      const scale = T.SMOKE_SCALE_MIN + Math.random() * (T.SMOKE_SCALE_MAX - T.SMOKE_SCALE_MIN);
      core.spawnParticleAtAngle(
        x + ox, y + oy,
        -Math.PI / 2 + (Math.random() - 0.5) * 1.0,
        0.3 + Math.random() * 0.5,
        T.SMOKE_COLOR, life, scale,
        -0.01, 0.95,
        false, false, T.SMOKE_ALPHA,
      );
    }
  }

  // --- Bright stretched streaks (tier 3+) — anime speed lines ---
  if (tier >= 3) {
    const streakCount = tier === 3 ? 3 + Math.floor(Math.random() * 2) : 5 + Math.floor(Math.random() * 2);
    for (let i = 0; i < streakCount; i++) {
      const spread = (Math.random() - 0.5) * 0.6;
      const ox = (Math.random() - 0.5) * 10;
      const oy = (Math.random() - 0.5) * 10;
      core.spawnParticleAtAngle(
        x + ox, y + oy,
        angle + spread, 0.2 + Math.random() * 0.5,
        Math.random() < 0.4 ? 0xffffff : playerColor,
        T.STREAK_LIFE, T.STREAK_SCALE,
        0, 0.92,
        true, true, T.STREAK_ALPHA, T.STREAK_STRETCH,
      );
    }
  }

  // --- Impact lines (tier 3+) — radiate backward ---
  if (tier >= 3) {
    const lineCount = tier === 3 ? 1 : 2;
    for (let i = 0; i < lineCount; i++) {
      const spread = (Math.random() - 0.5) * Math.PI * 0.4;
      const life = T.LINE_LIFE;
      core.impactLines.push({
        x, y,
        angle: backAngle + spread,
        innerRadius: 5,
        outerRadius: 15 + Math.random() * 10,
        expandSpeed: T.LINE_EXPAND + Math.random() * 20,
        color: Math.random() < 0.3 ? 0xffffff : playerColor,
        lineWidth: T.LINE_WIDTH,
        life, maxLife: life, freezeImmune: false,
      });
    }
  }

  // --- Afterimage glow (tier 4 only) ---
  if (tier >= 4) {
    core.spawnParticleAtAngle(
      x, y, 0, 0, playerColor,
      T.AFTERIMAGE_LIFE, T.AFTERIMAGE_SCALE,
      0, 1.0, true, false, T.AFTERIMAGE_ALPHA,
    );
  }
}

/**
 * Hop dust — small downward fan of dust on launch.
 * Follows spawnClimbDust pattern: 4 dark backing + 6 bright sparks.
 * Sakurai: dark+bright contrast, brief, reads as ground-kick.
 */
export function spawnHopDust(core: ParticleCore, cx: number, feetY: number, playerColor: number): void {
  const dustColors = [0x0a0c1e, 0x0c0e28, 0x1a1e40];

  // Layer 1: Dark dust — kicked downward/outward (NORMAL blend, Sakurai dark contrast)
  for (let i = 0; i < 4; i++) {
    const p = core.getInactive();
    if (!p) continue;
    const angle = Math.PI / 2 + (Math.random() - 0.5) * 1.2; // downward fan
    const speed = 1.5 + Math.random() * 2.0;
    configParticle(p, core, {
      x: cx + (Math.random() - 0.5) * 16,
      y: feetY - Math.random() * 4,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      color: dustColors[Math.floor(Math.random() * dustColors.length)],
      life: 0.12 + Math.random() * 0.1,
      scale: 0.25 + Math.random() * 0.3,
      alpha: 0.6 + Math.random() * 0.2,
      gravity: 0.1,
      friction: 0.94,
      rotationSpeed: (Math.random() - 0.5) * 8,
      addBlend: false,
    });
  }

  // Layer 2: Bright sparks — ADD blend, player color + white
  for (let i = 0; i < 6; i++) {
    const sparkColor = Math.random() < 0.3 ? 0xffffff : playerColor;
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.0; // upward-ish fan
    const speed = 3 + Math.random() * 4;
    core.spawnParticleAtAngle(
      cx + (Math.random() - 0.5) * 10, feetY,
      angle, speed,
      sparkColor,
      0.08 + Math.random() * 0.06,
      0.3 + Math.random() * 0.3,
      0.08, 0.93,
      true, true,
      0.85, 1.5,
    );
  }

  // Layer 3: Contact flash pop — brief glow at feet
  core.spawnParticle(
    cx, feetY, playerColor,
    0, 0.3,
    0.05, 0.08,
    1.8, 2.5,
    0, 0.3,
    true, false,
    0.45,
  );

  // Layer 4: Shockwave ring — tight, fast, brief (Sakurai: ground impact ring)
  core.spawnShockwave(cx, feetY + 2, playerColor, 30, 120, 1.8, 0.10);
}

/**
 * Lift trail — 4-layer directional exhaust with frame stagger + speed tiers.
 * Exhaust streams BEHIND the tank (opposite velocity). Faster = longer streaks + more particles.
 * Frame stagger (energy cape pattern): A frames = dark backing + core exhaust, B frames = bright streaks + edge sparks.
 * Sakurai: dark+bright contrast, directional communication, brief lifespans.
 */
export function spawnLiftTrail(core: ParticleCore, x: number, y: number, vx: number, vy: number, playerColor: number): void {
  const speed = Math.sqrt(vx * vx + vy * vy);
  const dirAngle = speed > 0.01 ? Math.atan2(vy, vx) : -Math.PI / 2;
  const exhaustAngle = dirAngle + Math.PI; // exhaust BEHIND tank
  const coneSpread = Math.PI / 3; // ±30° = 60° cone

  // Speed tier: hover (<2.0), cruise (2.0-4.0), fast (>=4.0)
  const tier = speed >= HOP.LIFT_TRAIL_TIER_FAST ? 2 : speed >= HOP.LIFT_TRAIL_TIER_CRUISE ? 1 : 0;

  // Spawn offset behind tank (further at higher speed)
  const spawnDist = 8 + speed * 2;
  const sx = x + Math.cos(exhaustAngle) * spawnDist;
  const sy = y + Math.sin(exhaustAngle) * spawnDist;

  // Stretch multiplier — faster = longer streaks
  const stretchMult = 1.0 + Math.min(speed / 5.0, 1.0);

  // Frame stagger — halves per-frame particle count
  liftTrailFrame++;
  const frameA = (liftTrailFrame & 1) === 0; // dark backing + core exhaust
  const frameB = !frameA;                     // bright streaks + edge sparks

  if (frameA) {
    // Layer 1: Dark backing (NORMAL blend, near-black — Sakurai depth framing)
    const darkCount = tier === 0 ? 2 : tier === 1 ? 3 : 4;
    for (let i = 0; i < darkCount; i++) {
      const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
      const spd = 0.8 + Math.random() * 1.5;
      // Offset along exhaust vector to avoid center overlap (Sakurai: spread at spawn)
      const spreadDist = Math.random() * 12;
      core.spawnParticleAtAngle(
        sx + Math.cos(exhaustAngle) * spreadDist + (Math.random() - 0.5) * 6,
        sy + Math.sin(exhaustAngle) * spreadDist + (Math.random() - 0.5) * 6,
        angle, spd,
        0x0a0a14,
        0.18 + Math.random() * 0.10,
        0.6 + Math.random() * 0.4,
        -0.01, 0.93,
        false, false, // NORMAL blend, soft texture
        0.5,
        1.3 + Math.random() * 0.5, // slight stretch along exhaust — reads as flow, not bubbles
      );
    }

    // Layer 2: Core exhaust (ADD blend, soft glow — gold/player)
    const coreCount = tier === 0 ? 3 : tier === 1 ? 4 : 6;
    for (let i = 0; i < coreCount; i++) {
      const color = Math.random() < 0.4 ? 0xffd700 : playerColor;
      const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
      const spd = 0.5 + Math.random() * 1.2;
      // Offset along exhaust vector to avoid center overlap (Sakurai: spread at spawn)
      const spreadDist = Math.random() * 14;
      core.spawnParticleAtAngle(
        sx + Math.cos(exhaustAngle) * spreadDist + (Math.random() - 0.5) * 8,
        sy + Math.sin(exhaustAngle) * spreadDist + (Math.random() - 0.5) * 8,
        angle, spd,
        color,
        0.12 + Math.random() * 0.10,
        0.4 + Math.random() * 0.3,
        0, 0.93,
        true, false, // ADD blend, soft texture
        0.55,
        1.4 + Math.random() * 0.4, // slight stretch — directional exhaust stream
      );
    }
  }

  if (frameB) {
    // Layer 3: Bright streaks (ADD blend, hard glow, stretched — anime speed lines)
    const streakCount = tier === 0 ? 2 : tier === 1 ? 3 : 5;
    for (let i = 0; i < streakCount; i++) {
      const color = Math.random() < 0.3 ? 0xffffff : (Math.random() < 0.5 ? 0xffd700 : playerColor);
      const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
      const spd = 1.0 + Math.random() * 2.0;
      core.spawnParticleAtAngle(
        sx + (Math.random() - 0.5) * 8,
        sy + (Math.random() - 0.5) * 8,
        angle, spd,
        color,
        0.10 + Math.random() * 0.06,
        0.3 + Math.random() * 0.2,
        0.04, 0.92,
        true, true, // ADD blend, hard texture
        0.85,
        (2.5 + Math.random() * 1.5) * stretchMult, // stretched along exhaust direction
      );
    }

    // Layer 4: Edge sparks (ADD blend, hard, tiny crisp accents)
    const sparkCount = tier === 0 ? 1 : tier === 1 ? 2 : 3;
    for (let i = 0; i < sparkCount; i++) {
      const color = Math.random() < 0.5 ? 0xffffff : 0xffd700;
      const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread * 1.2;
      const spd = 1.5 + Math.random() * 2.5;
      core.spawnParticleAtAngle(
        sx + (Math.random() - 0.5) * 6,
        sy + (Math.random() - 0.5) * 6,
        angle, spd,
        color,
        0.08 + Math.random() * 0.04,
        0.25 + Math.random() * 0.15,
        0.06, 0.92,
        true, true, // ADD blend, hard texture
        0.9,
        (1.8 + Math.random() * 0.7) * stretchMult,
      );
    }
  }
}

/**
 * Lift activation burst — 5-layer jetpack ignition on lift OFF→ON.
 * Directional exhaust cone opposite to movement + momentum lines forward.
 * Sakurai: dark+bright contrast, directional exhaust, crisp geometry, brief lifespans.
 */
export function spawnLiftBurst(
  core: ParticleCore,
  x: number, y: number,
  vx: number, vy: number,
  playerColor: number,
): void {
  const speed = Math.sqrt(vx * vx + vy * vy);
  const dirAngle = speed > 0.01 ? Math.atan2(vy, vx) : -Math.PI / 2;
  const exhaustAngle = dirAngle + Math.PI; // exhaust BEHIND tank
  const coneSpread = Math.PI / 3; // ±30° = 60° cone

  // Layer 1: Dark exhaust cone (NORMAL blend, near-black — Sakurai dark framing for depth)
  for (let i = 0; i < 6; i++) {
    const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
    const spd = 1.5 + Math.random() * 3.0;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 8,
      y + (Math.random() - 0.5) * 8,
      angle, spd,
      0x0a0a14,
      0.15 + Math.random() * 0.07,
      0.6 + Math.random() * 0.4,
      -0.01, 0.93,
      false, false, // NORMAL blend, soft texture
      0.5,
      1.8 + Math.random() * 0.7, // stretched along exhaust
    );
  }

  // Layer 2: Bright exhaust cone (ADD blend, gold/white/player — anime ignition sparks)
  const brightCount = 8 + Math.floor(Math.random() * 3);
  for (let i = 0; i < brightCount; i++) {
    const color = Math.random() < 0.35 ? 0xffd700 : (Math.random() < 0.3 ? 0xffffff : playerColor);
    const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
    const spd = 2.5 + Math.random() * 4.0;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 8,
      y + (Math.random() - 0.5) * 8,
      angle, spd,
      color,
      0.10 + Math.random() * 0.06,
      0.35 + Math.random() * 0.25,
      0.06, 0.92,
      true, true, // ADD blend, hard texture
      0.9,
      2.0 + Math.random() * 1.5, // stretched — anime speed lines
    );
  }

  // Layer 3: Core ignition flash (ADD blend, soft, large, ultra-brief — Sakurai flash = impact)
  core.spawnParticle(
    x, y, 0xffd700,
    0, 0.3,
    0.08, 0.12,
    2.5, 3.5,
    0, 0.25,
    true, false, // ADD blend, soft texture — diffuse ignition glow
    0.6,
  );

  // Layer 4: Momentum lines — crisp straight lines radiating in travel direction (forward)
  const lineCount = 3 + Math.floor(Math.random() * 2);
  for (let i = 0; i < lineCount; i++) {
    const angle = dirAngle + (Math.random() - 0.5) * 0.6; // ±0.3 rad fan forward
    const life = 0.12 + Math.random() * 0.04;
    core.impactLines.push({
      x, y,
      angle,
      innerRadius: 8,
      outerRadius: 20 + Math.random() * 15,
      expandSpeed: 60 + Math.random() * 40,
      color: Math.random() < 0.4 ? 0xffffff : 0xffd700,
      lineWidth: 2 + Math.random(),
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }

  // Layer 5: Shockwave ring — brief expanding ring at ignition point
  core.spawnShockwave(x, y, playerColor, 40, 160, 2.0, 0.14);

  // Layer 6: End punctuation snap — Sakurai Phase 4: "sudden disappearance" termination
  // Delayed +100ms: hard bright pop to visually end the sequence (not a slow fade)
  core.pendingBlasts.push({
    timestamp: Date.now() + 100, callback: () => {
      core.spawnParticle(
        x, y, 0xffd700,
        0, 0.2,
        0.03, 0.05,    // ultra-brief snap
        1.2, 1.8,
        0, 0.3,
        true, true,     // ADD, hard — crisp end mark
        0.5,
      );
    }
  });
}

/**
 * Fast fall activation burst — downward-directed entry effect.
 * Sakurai: dark+bright contrast, directional (downward), brief flash = commitment read.
 *
 * Layer 1: Dark backing — near-black chunks fanning downward (NORMAL blend, Sakurai depth)
 * Layer 2: Bright cyan/white streaks downward (ADD blend, anime speed lines)
 * Layer 3: Flash pop at feet (ADD blend, soft glow — Sakurai: flash communicates state change)
 * Layer 4: Impact lines — crisp downward-radiating lines
 */
export function spawnFastFallActivation(core: ParticleCore, cx: number, cy: number, playerColor: number): void {
  // Layer 1: Dark backing — downward fan (NORMAL blend, Sakurai dark contrast)
  const dustColors = [0x0a0c1e, 0x0c0e28, 0x1a1e40];
  for (let i = 0; i < 4; i++) {
    const p = core.getInactive();
    if (!p) continue;
    const angle = Math.PI / 2 + (Math.random() - 0.5) * 0.8; // downward fan
    const speed = 1.5 + Math.random() * 2.0;
    configParticle(p, core, {
      x: cx + (Math.random() - 0.5) * 12,
      y: cy + Math.random() * 6,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      color: dustColors[Math.floor(Math.random() * dustColors.length)],
      life: 0.12 + Math.random() * 0.08,
      scale: 0.2 + Math.random() * 0.25,
      alpha: 0.6 + Math.random() * 0.2,
      gravity: 0.15,
      friction: 0.94,
      rotationSpeed: (Math.random() - 0.5) * 8,
      addBlend: false,
    });
  }

  // Layer 2: Bright downward streaks — cyan + white (ADD blend, stretched)
  for (let i = 0; i < 6; i++) {
    const color = Math.random() < 0.4 ? 0xffffff : (Math.random() < 0.5 ? 0x00ddff : playerColor);
    const angle = Math.PI / 2 + (Math.random() - 0.5) * 0.6; // downward fan
    const speed = 4 + Math.random() * 5;
    core.spawnParticleAtAngle(
      cx + (Math.random() - 0.5) * 10, cy,
      angle, speed,
      color,
      0.08 + Math.random() * 0.06,
      0.3 + Math.random() * 0.3,
      0.1, 0.92,
      true, true, // ADD blend, hard texture
      0.85,
      2.0 + Math.random() * 1.0, // stretched — anime speed lines
    );
  }

  // Layer 3: Flash pop — brief glow at body center
  core.spawnParticle(
    cx, cy, 0x00ddff,
    0, 0.3,
    0.06, 0.10,
    2.0, 2.8,
    0, 0.3,
    true, false, // ADD blend, soft texture — diffuse glow
    0.5,
  );

  // Layer 4: Impact lines — crisp downward lines
  const lineCount = 2 + Math.floor(Math.random() * 2);
  for (let i = 0; i < lineCount; i++) {
    const angle = Math.PI / 2 + (Math.random() - 0.5) * 0.5; // downward fan
    const life = 0.08 + Math.random() * 0.04;
    core.impactLines.push({
      x: cx, y: cy,
      angle,
      innerRadius: 5,
      outerRadius: 18 + Math.random() * 12,
      expandSpeed: 70 + Math.random() * 30,
      color: Math.random() < 0.3 ? 0xffffff : 0x00ddff,
      lineWidth: 1.5 + Math.random(),
      life,
      maxLife: life,
      freezeImmune: false,
    });
  }
}

/**
 * Fast fall trail — sustained downward-flowing wisps while fast falling.
 * Lighter than lift trail — simple upward exhaust (particles go UP as tank goes DOWN).
 * Sakurai: brief, directional, dark+bright contrast.
 */
export function spawnFastFallTrail(core: ParticleCore, x: number, y: number, vy: number, playerColor: number): void {
  const exhaustAngle = -Math.PI / 2; // upward (opposite to fall direction)
  const coneSpread = Math.PI / 4; // ±22.5° cone

  // Spawn offset above tank (exhaust trails behind)
  const sy = y - 12;

  // Layer 1: Dark backing (NORMAL blend, near-black — Sakurai depth)
  for (let i = 0; i < 2; i++) {
    const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
    const spd = 0.5 + Math.random() * 1.0;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 8, sy + (Math.random() - 0.5) * 4,
      angle, spd,
      0x0a0a14,
      0.12 + Math.random() * 0.06,
      0.4 + Math.random() * 0.3,
      -0.02, 0.93,
      false, false, // NORMAL blend, soft texture
      0.4,
      1.2 + Math.random() * 0.4,
    );
  }

  // Layer 2: Bright wisps (ADD blend, cyan/white — speed-line feel)
  for (let i = 0; i < 3; i++) {
    const color = Math.random() < 0.3 ? 0xffffff : (Math.random() < 0.5 ? 0x00ddff : playerColor);
    const angle = exhaustAngle + (Math.random() - 0.5) * coneSpread;
    const spd = 0.8 + Math.random() * 1.5;
    core.spawnParticleAtAngle(
      x + (Math.random() - 0.5) * 6, sy + (Math.random() - 0.5) * 4,
      angle, spd,
      color,
      0.08 + Math.random() * 0.06,
      0.25 + Math.random() * 0.2,
      0.03, 0.92,
      true, true, // ADD blend, hard texture
      0.7,
      1.8 + Math.random() * 0.8, // stretched upward
    );
  }
}
